/**
 * Centralized route constants for the application
 * Use these constants instead of hardcoded paths to prevent inconsistencies
 */

/**
 * Type-safe route parameter definitions
 */
export type RouteParams = {
  boardId: string;
  projectId: string;
  sessionId: string;
};

/**
 * Route parameter extraction helpers
 */
export const extractRouteParam = {
  boardId: (path: string): string | null => {
    const match = path.match(/^\/boards\/([^/]+)$/);
    return match ? match[1] : null;
  },
  projectId: (path: string): string | null => {
    const match = path.match(/^\/projects\/([^/]+)$/);
    return match ? match[1] : null;
  },
  sessionId: (path: string): string | null => {
    const match = path.match(/^\/sessions\/([^/]+)$/);
    return match ? match[1] : null;
  },
} as const;

/**
 * Validate UUID format for route parameters
 */
export const isValidUUID = (id: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(id);
};

export const ROUTES = {
  // Public routes
  HOME: '/',
  AUTH: '/auth',
  WAITLIST: '/waitlist',
  VERIFY_EMAIL: '/verify-email',
  VERIFY_MAGIC_LINK: '/verify-magic-link',
  RESET_PASSWORD: '/reset-password',
  NOT_FOUND: '*',

  // Protected routes
  DASHBOARD: '/dashboard',
  PROFILE: '/profile',
  SEARCH: '/search',
  TAKEOUT: '/takeout',
  
  // Board routes
  BOARDS_CREATE: '/boards/create',
  BOARD_DETAIL: (boardId: RouteParams['boardId']) => `/boards/${boardId}` as const,
  
  // Project routes
  PROJECTS: '/projects',
  PROJECT_DETAIL: (projectId: RouteParams['projectId']) => `/projects/${projectId}` as const,
  
  // Session routes
  SESSION_CHAT: (sessionId: RouteParams['sessionId']) => `/sessions/${sessionId}` as const,
  
  // Memories
  MEMORIES: '/memories',
  
  // Gamification
  ACHIEVEMENTS: '/achievements',
  NOTIFICATION_HISTORY: '/notifications',
  
  // Quick Start
  QUICK_START: '/quick-start',
  
  // Tests
  TESTS: '/tests',
  
  // Admin routes
  ADMIN: '/admin',
  ADMIN_IP_MANAGEMENT: '/admin/ip-management',
  ADMIN_PROMPT_MANAGEMENT: '/admin/prompt-management',
  ADMIN_ONBOARDING_ANALYTICS: '/admin/onboarding-analytics',
} as const;

/**
 * Helper to check if a path is an admin route
 */
export const isAdminRoute = (path: string): boolean => {
  return path.startsWith('/admin');
};

/**
 * Helper to check if a path is a protected route
 */
export const isProtectedRoute = (path: string): boolean => {
  const publicRoutes: string[] = [ROUTES.HOME, ROUTES.AUTH, ROUTES.WAITLIST, ROUTES.VERIFY_EMAIL, ROUTES.VERIFY_MAGIC_LINK, ROUTES.RESET_PASSWORD];
  return !publicRoutes.includes(path) && path !== ROUTES.NOT_FOUND;
};
